import React, { useState } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import { Card } from '@apigrowfinance/pbolt-card';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import CardsBlock from '@components/accountCardControlDetails/cardsBlock/CardsBlock';
import ControlConditions from '@components/controlDetails/components/conditionsBlock/ControlConditions';
import ControlPageHeader from '@components/controlDetails/components/ControlPageHeader';
import FlexControlLimitBar from '@components/controlDetails/components/flexControlLimitBar/FlexControlLimitBar';
import CustomerControlBodyDetails from '@components/controlDetails/customer/CustomerControlBodyDetails';
import CustomerControlEditModal from '@components/controlDetails/customer/CustomerControlEditModal';
import LoadingErrorMessage from '@components/LoadingErrorMessage/LoadingErrorMessage';
import { useAccount } from '@contexts/AccountProvider';
import FlexControlType from '@enums/flexControls/type';
import useGetCustomerDetails from '@hooks/queries/useGetCustomerDetails';
import useGetFlexControlCustomer from '@hooks/queries/useGetFlexControlCustomer';

const CustomerControl = () => {
    const [showEditModal, setShowEditModal] = useState(false);
    const { customerId, flexControlId } = useParams();
    const { account, isLoading: isLoadingAccount } = useAccount();

    const {
        data,
        isLoading: isLoadingFlexControlCustomer,
        isError: isErrorFlexControlCustomer,
    } = useGetFlexControlCustomer({
        accountId: account?.account_id,
        customerId: customerId,
        flexId: flexControlId,
    });

    const {
        data: customerData,
        isLoading: isLoadingUseGetCustomerDetails,
        isError: isErrorCustomerDetails,
    } = useGetCustomerDetails({
        customerId: parseFloat(customerId),
    });
    const isError = isErrorFlexControlCustomer || isErrorCustomerDetails;
    const isLoading =
        isLoadingAccount ||
        isLoadingFlexControlCustomer ||
        isLoadingUseGetCustomerDetails;
    const showGraph =
        !!data?.type && data?.type !== FlexControlType.RESTRICTION;

    if (isError) {
        return (
            <Card>
                <LoadingErrorMessage />
            </Card>
        );
    }

    return (
        <>
            <Box data-testid="heading-description">
                <Typography variant="overline">
                    {isLoading ? (
                        <Skeleton data-testid="header-skeleton" width={120} />
                    ) : (
                        <FormattedMessage
                            defaultMessage="{customer} controls"
                            id="CustomerControl.heading"
                            values={{
                                customer:
                                    customerData?.customer?.social_name ||
                                    customerData?.customer?.printed_name ||
                                    '',
                            }}
                        />
                    )}
                </Typography>
            </Box>
            <Card isBottomRadiusBorder={false} paddingSize="small">
                <Card isBottomRadiusBorder={false} paddingSize="small">
                    <ControlPageHeader
                        data={data}
                        isActive={data?.active}
                        isLoading={isLoading}
                        setShowEditModal={setShowEditModal}
                    />
                </Card>
            </Card>

            <Box height={2} />
            {showGraph && (
                <>
                    <Card isTopRadiusBorder={false} isIndented>
                        <FlexControlLimitBar flexControl={data} />
                    </Card>
                    <Box height={10} />
                </>
            )}
            <Card isTopRadiusBorder={false} isIndented>
                <CustomerControlBodyDetails
                    cardControlItem={data}
                    isLoading={isLoading}
                    noColumnsPerRow={5}
                />
            </Card>
            <Box height={10} />
            <ControlConditions
                cardControlRules={data?.conditions}
                isLoading={isLoading}
                noColumnsPerRow={5}
            />
            <Box height={10} />
            {!!data?.card_ids?.length && (
                <CardsBlock cardIDsWithFlexControls={data?.card_ids} />
            )}
            <CustomerControlEditModal
                cardControlItem={data}
                customerName={
                    customerData?.customer?.social_name ||
                    customerData?.customer?.printed_name ||
                    ''
                }
                customerId={parseFloat(customerId)}
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
            />
        </>
    );
};

export default CustomerControl;
